import { SENSOR_MEASURES } from "../config/sensors.config";

const HOUR_MS = 60 * 60 * 1000;
export const HEATMAP_TIME_ZONE = "Europe/Rome";

export const HEATMAP_MEASURES = [
  { ...SENSOR_MEASURES.soil_temperature, label: "Temperatura del suolo" },
  { ...SENSOR_MEASURES.soil_moisture, label: "Umidità del suolo" },
  {
    ...SENSOR_MEASURES.soil_electrical_conductivity,
    label: "Conducibilità del suolo",
  },
];

const dateFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: HEATMAP_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});
const hourFormatter = new Intl.DateTimeFormat("it-IT", {
  timeZone: HEATMAP_TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZoneName: "shortOffset",
});
const timestampFormatter = new Intl.DateTimeFormat("it-IT", {
  timeZone: HEATMAP_TIME_ZONE,
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  timeZoneName: "shortOffset",
});
const numberFormatter = new Intl.NumberFormat("it-IT", {
  maximumSignificantDigits: 4,
});

export const getLastCompleteHeatmapHour = (now = Date.now()) =>
  new Date(Math.floor(Number(now) / HOUR_MS) * HOUR_MS - HOUR_MS).toISOString();

export const getHeatmapDate = (timestamp) => {
  const parts = dateFormatter.formatToParts(new Date(timestamp));
  const part = (type) => parts.find((item) => item.type === type).value;
  return `${part("year")}-${part("month")}-${part("day")}`;
};

export const formatHeatmapTimestamp = (timestamp) =>
  timestampFormatter.format(new Date(timestamp));

// Iterate real UTC hours so both occurrences of the autumn DST hour remain selectable.
export const getHeatmapHourOptions = (date, now = Date.now()) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return [];
  const midnight = Date.parse(`${date}T00:00:00Z`);
  if (
    !Number.isFinite(midnight) ||
    new Date(midnight).toISOString().slice(0, 10) !== date
  )
    return [];
  const currentHour = Math.floor(Number(now) / HOUR_MS) * HOUR_MS;
  const options = [];
  for (let offset = -3; offset < 26; offset += 1) {
    const time = midnight + offset * HOUR_MS;
    if (time < currentHour && getHeatmapDate(time) === date) {
      options.push({
        value: new Date(time).toISOString(),
        label: hourFormatter.format(time),
      });
    }
  }
  return options;
};

export const formatHeatmapValue = (value) =>
  Number.isFinite(value)
    ? numberFormatter.format(value)
    : "Dati non disponibili";

const PALETTES = {
  YlOrRd: [
    "#ffffcc",
    "#ffeda0",
    "#fed976",
    "#feb24c",
    "#fd8d3c",
    "#fc4e2a",
    "#e31a1c",
    "#bd0026",
    "#800026",
  ],
  YlGnBu: [
    "#ffffd9",
    "#edf8b1",
    "#c7e9b4",
    "#7fcdbb",
    "#41b6c4",
    "#1d91c0",
    "#225ea8",
    "#253494",
    "#081d58",
  ],
  YlOrBr: [
    "#ffffe5",
    "#fff7bc",
    "#fee391",
    "#fec44f",
    "#fe9929",
    "#ec7014",
    "#cc4c02",
    "#993404",
    "#662506",
  ],
};

export const getHeatmapGradient = (palette) =>
  PALETTES[palette]
    ? `linear-gradient(to right, ${PALETTES[palette].join(", ")})`
    : null;

export const normalizeSensorInterpolation = (data, request) => {
  let valueRange;
  try {
    valueRange = JSON.parse(data?.value_range);
  } catch {}
  if (
    data?.field_uuid !== request.fieldUuid ||
    data?.measurement_name !== request.measurementName ||
    data?.output_crs !== "EPSG:3857" ||
    typeof data?.colormap_name !== "string" ||
    !Number.isFinite(Date.parse(data?.start_timestamp)) ||
    typeof data?.raster_base64 !== "string" ||
    !Array.isArray(data?.sensors) ||
    !Array.isArray(valueRange) ||
    valueRange.length !== 2 ||
    !valueRange.every(Number.isFinite) ||
    valueRange[0] > valueRange[1]
  ) {
    throw new Error("Risposta heatmap non valida");
  }
  return {
    ...data,
    valueRange,
    sensors: data.sensors
      .filter(
        (sensor) =>
          typeof sensor?.sensor_id === "string" && sensor.sensor_id.trim() &&
          Number.isFinite(sensor.latitude) &&
          Math.abs(sensor.latitude) <= 90 &&
          Number.isFinite(sensor.longitude) &&
          Math.abs(sensor.longitude) <= 180,
      )
      .map((sensor) => ({
        ...sensor,
        value: Number.isFinite(sensor.value) ? sensor.value : null,
      })),
  };
};
